import React from 'react';
import { ClusterMarker, clusterAngleFor, clusterBucketKey } from './ClusterMarker';
import { ClockRing } from './ClockRing';
import { useCitiesStore } from '@store/citiesStore';
import { useClockStore } from '@store/timeStore';
import { TimezoneData } from '@/api/timezone/timezoneData';

export function CityClocks() {
  const { getAdjustedTime } = useClockStore();
  const { cities, homeCity } = useCitiesStore();
  const baseTime = getAdjustedTime();

  // Group cities by 15-min angle bucket (96 buckets across the 24-hour dial).
  // Mirrors iOS `appMarkerClusters` (Swift line 299-323): each bucket renders
  // as a single ClusterMarker (primary capsule + cluster tab).
  const orderedBuckets: number[] = [];
  const buckets = new Map<number, TimezoneData[]>();

  Array.from(cities.values()).forEach((city) => {
    const angle = clusterAngleFor(city, baseTime);
    const key = clusterBucketKey(angle);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      orderedBuckets.push(key);
    }
    bucket.push(city);
  });

  // Promote home to primary (index 0) within whichever bucket it falls into.
  // Mirrors Swift line 315-318.
  if (homeCity) {
    const homeBucket = Array.from(buckets.values()).find((list) =>
      list.some((c) => c.locationKey === homeCity)
    );
    if (homeBucket) {
      const idx = homeBucket.findIndex((c) => c.locationKey === homeCity);
      if (idx > 0) {
        const [home] = homeBucket.splice(idx, 1);
        homeBucket.unshift(home);
      }
    }
  }

  // Orbit radius from iOS spec
  // (TimeYWimeyClockSurface.swift:355-357):
  //   appMarkerOrbitRadius = (faceSize/2) - (gradientInset/2)
  // With gradientInset = 8% of faceSize, orbit = 46% of dial diameter from
  // center → pills sit in the white border ring between the inner conic
  // gradient (inset 8%, radius 0-42% from center) and the outer dial edge
  // (50%). Pills are upright in screen space, so the orbit being close to
  // the rim is not a clipping concern — the pill body extends inward.
  const singleRingRadius = 46;

  return (
    <ClockRing>
      {orderedBuckets.map((key) => {
        const list = buckets.get(key)!;
        // Stable layout/key based on the primary city's locationKey so a
        // city dragging across a bucket boundary doesn't remount the cluster.
        return (
          <ClusterMarker
            key={list[0].locationKey}
            cities={list}
            baseTime={baseTime}
            homeCity={homeCity}
            radius={singleRingRadius}
          />
        );
      })}
    </ClockRing>
  );
}
